import { Box, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import ArrowUpRightIcon from '~icons/lucide/arrow-up-right';
import ClockPlusIcon from '~icons/lucide/clock-plus';
import SlidersIcon from '~icons/lucide/sliders-horizontal';
import Volume2Icon from '~icons/lucide/volume-2';
import VolumeOffIcon from '~icons/lucide/volume-off';

import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { useChatMute } from '#features/chats/hooks/useChatMute';
import { serverNow } from '#features/date/utils/serverClock';
import { Link } from '#features/routing';
import {
  InfoCircleIcon,
  LogOutIcon,
  MenuVerticalIcon,
  PhoneOffIcon,
  SettingsIcon,
} from '#features/ui';
import {
  ActionMenu,
  ActionMenuItem,
  ActionMenuSeparator,
} from '#features/ui/components/ActionMenu';
import { IconButton } from '#features/ui/components/IconButton';
import { useVoiceRoomOwner } from '#features/voice/components/VoiceRoomProvider';
import { useExtendSession } from '../hooks/useExtendSession';
import { LeaveSessionDialog, useLeaveSession } from './LeaveSessionAction';
import {
  canExtendSession,
  roomMenuItems,
  type RoomMenuItem,
  type SessionRoomPhase,
} from './RoomChrome';

/**
 * Кебаб комнаты: всё, что комната умеет делать с сессией, и ничего, что делается нажатием на
 * саму комнату.
 *
 * На телефоне действия открываются общей шторкой; на десктопе меню в портале на слое `skipNav`: штатный слой выпадающего меню – `dropdown` (1000),
 * а комната стоит на `modal` (1400), и без явного слоя меню ложится ПОД собственное окно, из
 * которого его открыли. Ровно эту цену уже заплатил список устройств голоса.
 *
 * Выходов из меню два, и путать их нельзя: «Выйти из голоса» отпускает микрофон и оставляет
 * человека в составе, «Выйти из сессии» забирает и место. Поэтому они разведены всем, чем можно:
 * разными группами (первый стоит с «Настройками звука», второй – за разделителем), разными
 * значками, цветом и вопросом. Опасное действие здесь ровно одно, и спрашивает только оно: место
 * в составе могут занять, пока человек соображает, что нажал.
 */

/** Строка меню. `leaveVoice` не приходит из `roomMenuItems` – см. `menuItemsWithVoiceExit`. */
export type RoomMenuRow = RoomMenuItem | 'leaveVoice';

/**
 * Куда встаёт «Выйти из голоса».
 *
 * Состав меню считает `roomMenuItems`: там всё, что зависит от фазы сессии и свободных мест.
 * Голосового выхода в том списке нет и быть не может – он зависит не от сессии, а от того,
 * слышно ли меня прямо сейчас, а про это фаза не знает ничего.
 *
 * Правило одно: пункт есть ровно тогда, когда я в голосе этой сессии. Серым он не бывает
 * никогда – мёртвая строка «Выйти из голоса» у человека, который в голос не заходил, учит, что
 * выход не работает, и стоит на месте, где через секунду он понадобится по-настоящему.
 */
export const menuItemsWithVoiceExit = (
  items: readonly RoomMenuItem[],
  inVoice: boolean
): RoomMenuRow[] => {
  if (!inVoice) {
    return [...items];
  }

  const sessionExit = items.indexOf('leave');

  /*
    Место – последнее в голосовой группе: сразу за «Настройками звука» и вплотную перед
    разделителем, за которым начинается группа сессии. Выхода из сессии в списке может не быть
    вовсе – так меню выглядит у закончившейся сессии, – а голос при этом ещё жив: соединением
    владеет `VoiceRoomProvider`, и статус сессии его не рвёт. Тогда пункт встаёт последним, иначе
    из голоса закончившейся сессии выйти будет неоткуда.
  */
  return sessionExit === -1
    ? [...items, 'leaveVoice']
    : [...items.slice(0, sessionExit), 'leaveVoice', ...items.slice(sessionExit)];
};

type SessionRoomMenuProps = {
  postId: string;
  sessionId: string;
  /** Автор сессии: ему выхода из сессии в меню нет (см. `roomMenuItems`). */
  hostId: string | null;
  phase: SessionRoomPhase;
  /**
   * Я в голосе этой сессии.
   *
   * От него зависят два пункта: «Настройки звука» без живого микрофона настраивать нечего, а
   * «Выйти из голоса» вне голоса не значит ничего – его в меню нет вовсе.
   */
  inVoice: boolean;
  /** Цель нажатия: 32 в шапке окна, 44 там, где к ней тянутся пальцем. */
  size?: 'desktop' | 'mobile';
  /**
   * Открыть `SessionDetailsDialog` – он же «Настройки сессии».
   *
   * Диалог живёт у хоста, а не здесь: на телефоне выбор пункта закрывает шторку, и диалог,
   * живший внутри неё, уехал бы вместе с ней. Пункт появляется только когда обработчик есть:
   * пункт меню, который ничего не открывает, хуже его отсутствия.
   */
  onSessionSettings?: () => void;
  /**
   * Открыть список устройств.
   *
   * Своего экземпляра `VoiceDevicePicker` здесь быть не может: переключение устройства работает
   * только у владельца соединения LiveKit (`useVoiceRoom` – синглтон), а владеет им ярус голоса.
   * Второй список молча не переключал бы микрофон – это хуже, чем недоступный пункт.
   *
   * Без обработчика пункта нет вовсе. Вечно серая строка учила бы, что «Настройки звука» в
   * комнате не работают, – а они работают, просто из яруса: шеврон у микрофона. Пункт вернётся,
   * когда ярус научится открывать список снаружи (см. отчёт).
   */
  onAudioSettings?: () => void;
  /**
   * Спросить подтверждение и выйти из сессии.
   *
   * Когда обработчик передан – диалог и мутация принадлежат вызывающему, и второго выхода в
   * приложении нет. Без него меню спрашивает и выходит само: так работает мобильная шторка,
   * которая ничего снаружи о выходе не знает. Оставлять обе дороги надолго нельзя – это две
   * копии одного действия; при слиянии остаётся первая.
   */
  onLeave?: () => void;
  /** Вышли из сессии своим диалогом – поверхности пора закрыться. */
  onLeft?: () => void;
  /**
   * Переписка этой сессии.
   *
   * Нужна для заглушения уведомлений. Пока сессия открыта, отдельного выхода из переписки нет:
   * участник выходит из сессии, а событие состава одновременно закрывает ему доступ к чату.
   * После завершения сессии самостоятельный выход остаётся в шапке самой переписки.
   */
  chatId?: string | null;
  /**
   * Время сессии - ради одного пункта, «Продлить на 30 минут»: он есть, пока сессия идёт и до
   * потолка в восемь часов от старта есть место.
   */
  startedAt?: string | null;
  expiredAt?: string | null;
};

export const SessionRoomMenu = ({
  postId,
  sessionId,
  hostId,
  phase,
  inVoice,
  size = 'desktop',
  onSessionSettings,
  onAudioSettings,
  onLeave,
  onLeft,
  chatId = null,
  startedAt = null,
  expiredAt = null,
}: SessionRoomMenuProps) => {
  const { t } = useLingui();
  const { profile } = useProfile();
  // Сам выход и его подтверждение – общие с меню шапки переписки сессии: там те же три шага и
  // тот же вопрос, а копия разошлась бы на первой правке.
  const leaveAction = useLeaveSession({ sessionId, onLeft });

  /*
    Выход из голоса берём у владельца соединения, а не пишем свой. Владелец один на всё
    приложение (`VoiceRoomProvider`), и его `leave` сперва гасит стор – то есть человек перестаёт
    быть слышен мгновенно, – и только потом сообщает серверу. Второй выход, написанный здесь,
    рвал бы медиа в другом порядке, и разница была бы слышна остальным.
  */
  const { leave: disconnectVoice } = useVoiceRoomOwner('SessionRoomMenu');

  // Та же машинка, что в шапке треда: настройка уведомлений одна на все поверхности.
  const { muted, isMuting, toggle: toggleMute } = useChatMute(chatId);

  // Keep the mutation mounted when the sheet closes, including its busy state.
  const { extend, isExtending } = useExtendSession({ sessionId, postId });

  const isHost = Boolean(hostId) && hostId === profile?.id;
  const rows = menuItemsWithVoiceExit(
    roomMenuItems({
      phase,
      hasAudioSettings: Boolean(onAudioSettings),
      isHost,
      hasChat: Boolean(chatId),
      canExtend: canExtendSession(startedAt, expiredAt, serverNow()),
    }),
    inVoice
  );
  const targetSize = size === 'mobile' ? '44px' : '32px';

  /*
    Пустое меню не рисуем вовсе (26.09.2026).

    Так выглядит закончившаяся сессия, в голосе которой я уже не сижу: действий над ней не
    осталось ни одного. Кебаб, открывающий пустоту, читается как сломанная кнопка, а до этого
    дня он открывал кое-что похуже - два пункта про сессию, которой уже нет: «Открыть сессию» и
    «Выйти из чата», второй из которых после первого же нажатия отвечал «не найдено».

    Диалоги подтверждения здесь же: звать их больше неоткуда, и висеть в дереве им незачем.
  */
  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      <Box flexShrink={0}>
        <ActionMenu
          title={t`post:session.room.menuAria`}
          trigger={
            <IconButton
              variant="ghost"
              onPointerDown={(event) => event.stopPropagation()}
              aria-label={t`post:session.room.menuAria`}
              w={targetSize}
              minW={targetSize}
              h={targetSize}
              css={{ '@media (any-pointer: coarse)': { minWidth: '44px', minHeight: '44px' } }}
              color="fg.icon"
            >
              <MenuVerticalIcon />
            </IconButton>
          }
        >
          {rows.map((item) => {
            switch (item) {
              case 'openSession':
                return (
                  /*
                      Ссылкой, а не обработчиком: у заявки есть адрес, и «открыть в новой вкладке»
                      средней кнопкой – ровно то, чего человек ждёт от строки «Открыть сессию».
                    */
                  <ActionMenuItem key={item} value={item} asChild>
                    <Link to="/{-$locale}/posts/$postId" params={{ postId }}>
                      <ArrowUpRightIcon />
                      {t`post:session.room.openSession`}
                    </Link>
                  </ActionMenuItem>
                );

              case 'sessionSettings':
                /*
                    Одно окно, два имени по роли (21.09.2026).

                    Раньше пункт назывался «Session settings» у всех, а на карточке та же кнопка
                    называлась «Manage»: одно окно под двумя именами на одном экране. Теперь имя
                    совпадает с карточкой и с тем, что человек за ним найдёт: автор управляет
                    сессией (состав, места, завершить), участник читает о ней (кто в составе,
                    когда, ранги) - настраивать ему там нечего.
                  */
                return onSessionSettings ? (
                  <ActionMenuItem key={item} value={item} onSelect={onSessionSettings}>
                    {isHost ? <SettingsIcon /> : <InfoCircleIcon />}
                    {isHost ? t`post:session.manage` : t`post:session.about`}
                  </ActionMenuItem>
                ) : null;

              case 'extend':
                /*
                    Продлить на полчаса. Без вопроса и без выбора длительности: жмут посреди
                    игры, и нужно больше - жмут ещё раз. Отказ сервера (упёрлись в восемь часов,
                    у кого-то из команды следом другая сессия) приходит тостом.
                  */
                return (
                  <ActionMenuItem
                    key={item}
                    value={item}
                    disabled={isExtending}
                    onSelect={() => void extend()}
                  >
                    <ClockPlusIcon />
                    {t`Extend by 30 minutes`}
                  </ActionMenuItem>
                );

              case 'muteChat':
                /*
                    Заглушить переписку сессии. Тот же пункт и та же машинка, что в шапке треда:
                    настройка уезжает на сервер и гасит вместе со звуком пуш, письмо и
                    колокольчик - на всех устройствах, а не только на этом.
                  */
                return (
                  <ActionMenuItem key={item} value={item} disabled={isMuting} onSelect={toggleMute}>
                    {muted ? <Volume2Icon /> : <VolumeOffIcon />}
                    {muted ? t`Unmute notifications` : t`Mute notifications`}
                  </ActionMenuItem>
                );

              case 'audioSettings':
                return (
                  <ActionMenuItem
                    key={item}
                    value={item}
                    // Вне голоса настраивать нечего: устройства выбираются у живого микрофона,
                    // а список без него отдал бы безымянные «Микрофон (2)» – браузер называет
                    // устройства только после выданного разрешения. Это единственная причина,
                    // по которой пункт бывает серым, и она объяснена подписью рядом.
                    disabled={!inVoice}
                    onSelect={() => onAudioSettings?.()}
                  >
                    <SlidersIcon />
                    {t`post:session.room.audioSettings`}
                    {inVoice ? null : (
                      <Text textStyle="caption" color="fg.muted" ml="auto">
                        {t`post:session.room.audioSettingsHint`}
                      </Text>
                    )}
                  </ActionMenuItem>
                );

              case 'leaveVoice':
                return (
                  /*
                      Без подтверждения – и это осознанно. Выход из голоса обратим одним
                      нажатием: место в составе остаётся за мной, а «Войти» стоит ровно там же,
                      в ярусе. Диалог на действии, которое за вечер делают десяток раз, – налог
                      на привычку; вопрос здесь задаёт только выход из сессии, потому что там
                      обратной дороги может и не быть – место займут.

                      Цветом не выделяем: красная строка означает в этом меню ровно одно – что
                      после неё придётся проситься обратно.
                    */
                  <ActionMenuItem key={item} value={item} onSelect={() => void disconnectVoice()}>
                    <PhoneOffIcon />
                    {t`post:session.room.leaveVoice`}
                  </ActionMenuItem>
                );

              case 'leave':
                return (
                  <Box key={item}>
                    <ActionMenuSeparator />
                    {/*
                        Опасное – текстом, а не заливкой: красная кнопка в списке из пяти строк
                        притягивает нажатие, а это единственный пункт, который нельзя нажать
                        случайно. `fg.error` – смысловой токен той же красной ступени: голых
                        ступеней палитры в разметке не держим.
                      */}
                    <ActionMenuItem
                      value={item}
                      color="fg.error"
                      onSelect={() => (onLeave ? onLeave() : leaveAction.ask())}
                    >
                      <LogOutIcon />
                      {t`post:session.room.leave`}
                    </ActionMenuItem>
                  </Box>
                );

              default:
                return null;
            }
          })}
        </ActionMenu>
      </Box>

      {/* Свой диалог – только там, где выходом никто не владеет снаружи (мобильная шторка). */}
      <LeaveSessionDialog {...leaveAction} />
    </>
  );
};
